import { createFileRoute } from "@tanstack/react-router";
import {
	Building2,
	CheckCircle2,
	ChevronRight,
	Circle,
	Download,
	FileText,
	Loader2,
	Upload,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";

// Local type
interface Organization {
	name: string;
}

export const Route = createFileRoute("/public/vendors/onboarding")({
	component: VendorOnboardingPage,
});

interface OnboardingStep {
	id: string;
	title: string;
	description: string;
	type: "form" | "document" | "certification";
	required: boolean;
	completed: boolean;
}

function VendorOnboardingPage() {
	// Organization would come from URL params or tRPC query in production
	const organization: Organization = { name: "Buyer Organization" };

	const [currentStep, setCurrentStep] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [steps, setSteps] = useState<OnboardingStep[]>([
		{
			id: "company-info",
			title: "Company Information",
			description: "Basic company details and contact information",
			type: "form",
			required: true,
			completed: false,
		},
		{
			id: "w9-form",
			title: "W-9 Form",
			description: "Tax identification and certification",
			type: "document",
			required: true,
			completed: false,
		},
		{
			id: "insurance",
			title: "Insurance Certificate",
			description: "Proof of liability insurance",
			type: "document",
			required: true,
			completed: false,
		},
		{
			id: "certifications",
			title: "Business Certifications",
			description: "MBE, WBE, SBE, or other certifications (optional)",
			type: "certification",
			required: false,
			completed: false,
		},
		{
			id: "vendor-agreement",
			title: "Vendor Agreement",
			description: "Review and accept the vendor terms and conditions",
			type: "form",
			required: true,
			completed: false,
		},
	]);

	// Form state for company info
	const [companyInfo, setCompanyInfo] = useState({
		name: "",
		dba: "",
		address: "",
		city: "",
		state: "",
		zip: "",
		phone: "",
		email: "",
		website: "",
		taxId: "",
		primaryContact: "",
		primaryContactEmail: "",
		primaryContactPhone: "",
	});

	// Certifications state
	const [certifications, setCertifications] = useState({
		mbe: false,
		wbe: false,
		sbe: false,
		sdvosb: false,
		vbe: false,
		other: "",
	});

	// Agreement state
	const [agreementAccepted, setAgreementAccepted] = useState(false);

	const completedSteps = steps.filter((s) => s.completed).length;
	const totalSteps = steps.length;
	const progressPercent = (completedSteps / totalSteps) * 100;

	const handleStepComplete = (stepId: string) => {
		setSteps((prev) =>
			prev.map((s) => (s.id === stepId ? { ...s, completed: true } : s)),
		);
		if (currentStep < steps.length - 1) {
			setCurrentStep((prev) => prev + 1);
		}
		toast.success("Step completed!");
	};

	const handleSubmitOnboarding = async () => {
		const requiredIncomplete = steps.filter((s) => s.required && !s.completed);
		if (requiredIncomplete.length > 0) {
			toast.error("Please complete all required steps before submitting.");
			return;
		}

		setIsSubmitting(true);
		await new Promise((resolve) => setTimeout(resolve, 2000));
		setIsSubmitting(false);
		toast.success(
			"Onboarding complete! You can now submit bids to this organization.",
		);
	};

	const currentStepData = steps[currentStep];

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-5xl mx-auto px-4 py-4 sm:px-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
								<span className="text-primary-foreground font-bold">B</span>
							</div>
							<div>
								<h1 className="font-semibold text-lg">Vendor Onboarding</h1>
								<p className="text-sm text-muted-foreground">
									{organization.name}
								</p>
							</div>
						</div>
					</div>
				</div>
			</header>

			<main className="max-w-5xl mx-auto px-4 py-8 sm:px-6">
				<div className="grid gap-6 md:grid-cols-3">
					{/* Progress Sidebar */}
					<div className="md:col-span-1">
						<Card>
							<CardHeader>
								<CardTitle className="text-lg">Progress</CardTitle>
								<CardDescription>
									Complete all required steps to start bidding
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="mb-4">
									<div className="flex justify-between text-sm mb-1">
										<span>
											{completedSteps} of {totalSteps} completed
										</span>
										<span>{Math.round(progressPercent)}%</span>
									</div>
									<Progress value={progressPercent} />
								</div>

								<div className="space-y-2">
									{steps.map((step, index) => (
										<button
											type="button"
											key={step.id}
											onClick={() => setCurrentStep(index)}
											className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
												currentStep === index
													? "bg-primary/10 border border-primary"
													: "hover:bg-muted"
											}`}
										>
											{step.completed ? (
												<CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
											) : (
												<Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
											)}
											<div className="flex-1 min-w-0">
												<p
													className={`text-sm font-medium truncate ${
														step.completed ? "text-green-700" : ""
													}`}
												>
													{step.title}
												</p>
												{!step.required && (
													<Badge variant="outline" className="text-xs mt-1">
														Optional
													</Badge>
												)}
											</div>
											<ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
										</button>
									))}
								</div>
							</CardContent>
						</Card>
					</div>

					{/* Main Content Area */}
					<div className="md:col-span-2">
						<Card>
							<CardHeader>
								<div className="flex items-center gap-2">
									{currentStepData.type === "form" && (
										<FileText className="h-5 w-5" />
									)}
									{currentStepData.type === "document" && (
										<Upload className="h-5 w-5" />
									)}
									{currentStepData.type === "certification" && (
										<Building2 className="h-5 w-5" />
									)}
									<CardTitle>{currentStepData.title}</CardTitle>
								</div>
								<CardDescription>{currentStepData.description}</CardDescription>
							</CardHeader>
							<CardContent>
								{/* Company Information Form */}
								{currentStepData.id === "company-info" && (
									<div className="space-y-4">
										<div className="grid gap-4 md:grid-cols-2">
											<div className="space-y-2">
												<Label htmlFor="name">Company Name *</Label>
												<Input
													id="name"
													value={companyInfo.name}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															name: e.target.value,
														}))
													}
													placeholder="Your Company, Inc."
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor="dba">DBA (if applicable)</Label>
												<Input
													id="dba"
													value={companyInfo.dba}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															dba: e.target.value,
														}))
													}
													placeholder="Doing Business As"
												/>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="address">Street Address *</Label>
											<Input
												id="address"
												value={companyInfo.address}
												onChange={(e) =>
													setCompanyInfo((prev) => ({
														...prev,
														address: e.target.value,
													}))
												}
												placeholder="123 Business Ave"
											/>
										</div>

										<div className="grid gap-4 md:grid-cols-3">
											<div className="space-y-2">
												<Label htmlFor="city">City *</Label>
												<Input
													id="city"
													value={companyInfo.city}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															city: e.target.value,
														}))
													}
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor="state">State *</Label>
												<Input
													id="state"
													value={companyInfo.state}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															state: e.target.value,
														}))
													}
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor="zip">ZIP Code *</Label>
												<Input
													id="zip"
													value={companyInfo.zip}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															zip: e.target.value,
														}))
													}
												/>
											</div>
										</div>

										<div className="grid gap-4 md:grid-cols-2">
											<div className="space-y-2">
												<Label htmlFor="phone">Phone *</Label>
												<Input
													id="phone"
													type="tel"
													value={companyInfo.phone}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															phone: e.target.value,
														}))
													}
													placeholder="(555) 123-4567"
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor="email">Email *</Label>
												<Input
													id="email"
													type="email"
													value={companyInfo.email}
													onChange={(e) =>
														setCompanyInfo((prev) => ({
															...prev,
															email: e.target.value,
														}))
													}
												/>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="taxId">Tax ID / EIN *</Label>
											<Input
												id="taxId"
												value={companyInfo.taxId}
												onChange={(e) =>
													setCompanyInfo((prev) => ({
														...prev,
														taxId: e.target.value,
													}))
												}
												placeholder="XX-XXXXXXX"
											/>
										</div>

										<Button
											className="w-full"
											onClick={() => handleStepComplete("company-info")}
										>
											Save & Continue
										</Button>
									</div>
								)}

								{/* Document Upload Steps */}
								{(currentStepData.id === "w9-form" ||
									currentStepData.id === "insurance") && (
									<div className="space-y-4">
										{currentStepData.id === "w9-form" && (
											<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
												<p className="text-sm text-blue-800">
													Download the W-9 form template, fill it out, and
													upload the completed form below.
												</p>
												<Button variant="outline" size="sm" className="mt-2">
													<Download className="mr-2 h-4 w-4" />
													Download W-9 Template
												</Button>
											</div>
										)}

										<div className="border-2 border-dashed rounded-lg p-8 text-center">
											<Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
											<p className="font-medium">
												Drop files here or click to upload
											</p>
											<p className="text-sm text-muted-foreground mt-1">
												PDF, DOC, or DOCX up to 500MB
											</p>
											<Input
												type="file"
												className="hidden"
												id="file-upload"
												accept=".pdf,.doc,.docx"
												onChange={() =>
													toast.success("File uploaded successfully!")
												}
											/>
											<Button asChild variant="outline" className="mt-4">
												<label htmlFor="file-upload" className="cursor-pointer">
													Select File
												</label>
											</Button>
										</div>

										<Button
											className="w-full"
											onClick={() => handleStepComplete(currentStepData.id)}
										>
											Upload & Continue
										</Button>
									</div>
								)}

								{/* Certifications */}
								{currentStepData.id === "certifications" && (
									<div className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Select any certifications your company holds. These can
											help you qualify for certain opportunities.
										</p>

										<div className="space-y-3">
											{[
												{
													id: "mbe",
													label: "MBE - Minority Business Enterprise",
												},
												{
													id: "wbe",
													label: "WBE - Woman-Owned Business Enterprise",
												},
												{ id: "sbe", label: "SBE - Small Business Enterprise" },
												{
													id: "sdvosb",
													label:
														"SDVOSB - Service-Disabled Veteran-Owned Small Business",
												},
												{
													id: "vbe",
													label: "VBE - Veteran Business Enterprise",
												},
											].map((cert) => (
												<div
													key={cert.id}
													className="flex items-center space-x-2"
												>
													<Checkbox
														id={cert.id}
														checked={
															certifications[
																cert.id as keyof typeof certifications
															] as boolean
														}
														onCheckedChange={(checked) =>
															setCertifications((prev) => ({
																...prev,
																[cert.id]: checked,
															}))
														}
													/>
													<Label htmlFor={cert.id} className="font-normal">
														{cert.label}
													</Label>
												</div>
											))}
										</div>

										<div className="space-y-2">
											<Label htmlFor="other-cert">Other Certifications</Label>
											<Textarea
												id="other-cert"
												value={certifications.other}
												onChange={(e) =>
													setCertifications((prev) => ({
														...prev,
														other: e.target.value,
													}))
												}
												placeholder="List any other certifications..."
												rows={3}
											/>
										</div>

										<Button
											className="w-full"
											onClick={() => handleStepComplete("certifications")}
										>
											Save & Continue
										</Button>
									</div>
								)}

								{/* Vendor Agreement */}
								{currentStepData.id === "vendor-agreement" && (
									<div className="space-y-4">
										<div className="bg-muted p-4 rounded-lg max-h-64 overflow-y-auto">
											<h4 className="font-medium mb-2">
												Vendor Terms and Conditions
											</h4>
											<div className="text-sm text-muted-foreground space-y-2">
												<p>
													By registering as a vendor with {organization.name},
													you agree to the following terms:
												</p>
												<p>
													1. You certify that all information provided is
													accurate and complete.
												</p>
												<p>
													2. You agree to comply with all applicable laws and
													regulations.
												</p>
												<p>
													3. You understand that submission of false information
													may result in disqualification.
												</p>
												<p>
													4. You agree to maintain current insurance coverage as
													required.
												</p>
												<p>
													5. You acknowledge that award decisions are final and
													at the sole discretion of the organization.
												</p>
												<p>
													6. You agree to non-collusion and fair competitive
													practices.
												</p>
											</div>
										</div>

										<div className="flex items-center space-x-2">
											<Checkbox
												id="accept-terms"
												checked={agreementAccepted}
												onCheckedChange={(checked) =>
													setAgreementAccepted(checked as boolean)
												}
											/>
											<Label htmlFor="accept-terms">
												I have read and accept the Vendor Terms and Conditions
											</Label>
										</div>

										<Button
											className="w-full"
											disabled={!agreementAccepted}
											onClick={() => handleStepComplete("vendor-agreement")}
										>
											Accept & Continue
										</Button>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Submit Button */}
						{completedSteps === totalSteps && (
							<Card className="mt-4">
								<CardContent className="pt-6">
									<div className="text-center space-y-4">
										<CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
										<h3 className="text-lg font-medium">
											All Steps Completed!
										</h3>
										<p className="text-muted-foreground">
											You're ready to submit your onboarding and start bidding.
										</p>
										<Button
											size="lg"
											onClick={handleSubmitOnboarding}
											disabled={isSubmitting}
										>
											{isSubmitting ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													Submitting...
												</>
											) : (
												"Complete Onboarding"
											)}
										</Button>
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				</div>
			</main>
		</div>
	);
}
